// screens/ProfileScreen.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector }    from 'react-redux';
import authService        from '../services/authService';
import ProfileTemplate    from '../templates/ProfileTemplate/ProfileTemplate';
import useAppWebSocket    from '../hooks/useWebSocket';

// ── Hors composant → jamais recréé ──────────────────────────────────
const buildSnapshot = (p) => ({
  name:         p.name         || '',
  lastname:     p.lastname     || '',
  adress:       p.adress       || '',
  city:         p.city         || '',
  country:      p.country      || '',
  organization: p.organization || '',
});

// Mappe le status HTTP → message lisible (responsabilité séparée)
const getPasswordError = (error) => {
  const status = error?.response?.status || error?.status;
  if (status === 403) return 'Mot de passe actuel incorrect.';
  return error?.message || 'Erreur lors du changement de mot de passe.';
};

// ────────────────────────────────────────────────────────────────────

const ProfileScreen = ({ onClose }) => {
  const { user: currentUser } = useSelector((s) => s.auth);

  // ─── State ────────────────────────────────────────────────────────
  const [profile,     setProfile]     = useState(null);
  const [stats,       setStats]       = useState(null);
  const [recent,      setRecent]      = useState([]);
  const [loading,     setLoading]     = useState(true);

  // Séparation claire : saving profil vs saving password
  const [savingProfile,  setSavingProfile]  = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const [tab,         setTab]         = useState('activity');
  const [pwdError,    setPwdError]    = useState('');
  const [formData,    setFormData]    = useState({});
  const [initialData, setInitialData] = useState({});

  // ─── WebSocket ────────────────────────────────────────────────────

  // activity.log → prepend le nouveau log si c'est le user courant
  const handleActivityLog = useCallback((data) => {
    if (!currentUser || String(data.user_id) !== String(currentUser.id)) return;
    setRecent((prev) => [{
      service:   data.service_name,
      timestamp: data.timestamp,
      details:   data.details,
    }, ...prev].slice(0, 50));
  }, [currentUser]);

  // stats.updated → met à jour le total sans re-fetch
  const handleStatsUpdated = useCallback((data) => {
    if (!currentUser || String(data.user_id) !== String(currentUser.id)) return;
    setStats((prev) =>
      prev ? { ...prev, total_api_calls: data.alltime_total } : prev
    );
  }, [currentUser]);

  useAppWebSocket({
    'activity.log':  handleActivityLog,
    'stats.updated': handleStatsUpdated,
  });

  // ─── Fetch ────────────────────────────────────────────────────────

  // Pas de useCallback ici → loadProfileData n'est pas passée
  // en prop ni utilisée dans un autre useCallback
  const loadProfileData = async () => {
    setLoading(true);
    try {
      const [profileRes, statsRes, recentRes] = await Promise.all([
        authService.getMyProfile(),
        authService.getMyStats(),
        authService.getMyRecentActivity(),
      ]);
      setProfile(profileRes);
      setStats(statsRes);
      setRecent(recentRes);
      const snapshot = buildSnapshot(profileRes);
      setFormData(snapshot);
      setInitialData(snapshot);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProfileData(); }, []); // eslint-disable-line

  // ─── Handlers ─────────────────────────────────────────────────────
  const handleProfileChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      await authService.updateMyProfile(formData);
      await loadProfileData(); // Recharge pour avoir les données fraîches
      setTab('activity');
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async (passwordForm) => {
    setSavingPassword(true);
    setPwdError(''); // Reset l'erreur précédente avant chaque tentative
    try {
      await authService.updateMyPassword({
        id:       profile.id,
        old_pass: passwordForm.old_password,
        new_pass: passwordForm.new_password,
      });
      setTab('activity');
    } catch (error) {
      setPwdError(getPasswordError(error));
    } finally {
      setSavingPassword(false);
    }
  };

  // ─── Rendu ────────────────────────────────────────────────────────
  return (
    <ProfileTemplate
      profile={profile}
      stats={stats}
      recent={recent}
      loading={loading}
      savingProfile={savingProfile}
      savingPassword={savingPassword}
      tab={tab}
      onTabChange={setTab}
      formData={formData}
      initialData={initialData}
      onProfileChange={handleProfileChange}
      onSaveProfile={handleSaveProfile}
      onSavePassword={handleSavePassword}
      pwdError={pwdError}
      onClose={onClose}
    />
  );
};

export default ProfileScreen;